import {useCallback, useEffect, useRef, useState} from 'react';

import {getUsersByEmails} from 'src/openapi/admin/apis/UserIdentityApi';
import {useAppDispatch} from 'src/store';

import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Return = {
    searchUsers: (email: string) => Promise<UserShortInfo[]>,
    loading: boolean,
    results: UserShortInfo[],
    error: string | null,
};

export const useUserSearch = (): Return => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<UserShortInfo[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef(0);

    const cancelOngoingRequest = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    }, []);

    useEffect(() => () => cancelOngoingRequest(), [cancelOngoingRequest]);

    const searchUsers = useCallback(async (email: string): Promise<UserShortInfo[]> => {
        const trimmedEmail = email.trim();
        const requestId = ++requestIdRef.current;
        cancelOngoingRequest();

        if (!trimmedEmail) {
            setResults([]);
            setError(null);
            setLoading(false);
            return [];
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const res = await dispatch(getUsersByEmails({userEmail: trimmedEmail}, {signal: controller.signal}));

            if (controller.signal.aborted || requestId !== requestIdRef.current) {
                return [];
            }

            const users = res.payload?.success ? (res.payload.data as UserShortInfo[] | undefined) : undefined;

            if (!users || users.length === 0) {
                setResults([]);
                return [];
            }

            setResults(users);
            return users;
        } catch (err) {
            if (controller.signal.aborted || requestId !== requestIdRef.current) {
                return [];
            }

            const message = err instanceof Error ? err.message : String(err);
            setError(message);
            setResults([]);
            return [];
        } finally {
            if (requestId === requestIdRef.current && !controller.signal.aborted) {
                setLoading(false);
                abortControllerRef.current = null;
            }
        }
    }, [cancelOngoingRequest, dispatch]);

    return {searchUsers, loading, results, error};
};
